import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import values from 'ramda/src/values';
import Button from '@components/@core/AnimatedButton';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import ResponsiveTabs from '@components/@core/ResponsiveTabs';
import Tab from '@components/@core/Tab';
import AppActions from '~/actions/app';
import CommunityActions from '~/actions/community';
import VendorActions from '~/actions/vendor';
import { BLOG_MOBILE_TABLIST } from '~/services/constants';
import { api } from '~/sagas';
import Recent from './Recent';
import Detail from './Detail';
import Categories, { CategoryFeed } from './Categories';
import Departments, { DepartmentFeed } from './Departments';

const Blog = ({ editable, data, premiumBlogs }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const params = useParams();
    const blogHeaderRef = useRef(null);

    const categories = data?.blog_categories ? values(data?.blog_categories) : [];
    const departments = useSelector((state) => state.company.blogCategories) || [];
    const dataPosts = data?.posts?.data || [];
    const recentBlogs = useSelector((state) => state.community.recentBlogs) || {};
    const recentPosts = recentBlogs?.posts?.data || [];
    const post = [...dataPosts, ...recentPosts].find((item) => item.slug === params.post);

    const [active, setActive] = useState('recent');
    const [detailPost, setDetailPost] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [allcategories, setAllCategories] = useState(false);

    const handleTab = (e) => {
        setActive(e.id);
        setSelectedCategory(null);
        setSelectedDepartment(null);
    };

    const handleSelectCategory = (category) => () => {
        setActive('categories');
        setSelectedCategory(category);
        setSelectedDepartment(null);
    };

    const handleGetCategoryDetail = (category) => {
        dispatch(CommunityActions.getblogdetailsRequest(data.user_id, { blogId: data.blog?.id, blogCategories: category.name }, handleSelectCategory(category)));
    };

    const handleSelectDepartment = (department) => () => {
        setActive('departments');
        setSelectedCategory(null);
        setSelectedDepartment(department);
    };

    const handleGetDepartmentDetail = (department) => {
        dispatch(CommunityActions.getblogdetailsRequest(data.user_id, { blogId: data.blog?.id, categoryId: department.id }, handleSelectDepartment(department)));
    };

    const handlePremiumBlog = (e) => {
        dispatch(CommunityActions.getcommunityuserRequest({ userId: e.author.id, blogId: e.blog.id }));
        window.location = e?.data?.permalink;
    };

    const handleSelectMenu = (index) => (item) => (e) => {
        switch (e) {
            case 'delete':
                handleDeleteConfirm(index);
                break;

            case 'edit':
                handleAddCategory(item, index);
                break;

            default:
                break;
        }
    };

    const handleAddCategory = (item, index = -1) => {
        if (active === 'categories') {
            const payload = {
                type: 'Add Category',
                data: {
                    before: {
                        type: 1,
                        data: item,
                        index,
                        blog: data,
                        companyId: data.id,
                        userId: data.user_id,
                    },
                    after: { type: 'GETCOMMUNITYUSER_REQUEST', payload: { userId: data.user_id } },
                },
            };
            dispatch(AppActions.modalRequest(payload));
        }
    };

    const handleDeleteConfirm = (idx) => {
        const payload = {
            type: 'Confirm',
            data: { before: { title: 'Delete', body: 'Are you sure you want to delete this item?' } },
            callBack: {
                onYes: () => handleDeleteCategory(idx),
            },
        };
        dispatch(AppActions.modalRequest(payload));
    };

    const handleDeleteCategory = (idx) => {
        if (active === 'categories') {
            const newCategories = categories.map((e) => e.name);
            newCategories.splice(idx, 1);
            const payload = {
                data: { blog: { ...data.blog, data: { categories: newCategories } } },
                after: { type: 'GETCOMMUNITYUSER_REQUEST', payload: { userId: data.user_id } },
            };
            dispatch(VendorActions.saveblogRequest(payload));
        }
    };

    const handleFetchDetailPost = async () => {
        if (params?.entity && params?.post) {
            const res = await api.getBlogSlug(params.entity, params.post);
            setDetailPost(res?.data?.posts?.data[0]);
        }
    };

    useEffect(() => {
        handleFetchDetailPost();
    }, [post?.comments]);

    useEffect(() => {
        if (blogHeaderRef?.current) {
            blogHeaderRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [detailPost?.id, active]);

    useEffect(() => {
        if (location.state?.departments) {
            handleGetDepartmentDetail(location.state.departments);
        }
    }, [location.state?.departments]);

    return (
        <ErrorBoundary className="individual-profile-blog d-flex ssm-column">
            <div className="d-flex-3 d-flex-sm-3 me-1" ref={blogHeaderRef}>
                <div className="card-header d-h-between">
                    <span className="dsl-b22 bold">My Blog</span>
                </div>
                <ResponsiveTabs className="card-content" active={active} data={BLOG_MOBILE_TABLIST} onTab={handleTab} />
                <Tab eventKey="recent" active={active}>
                    {detailPost ? (
                        <Detail data={detailPost} posts={premiumBlogs} onRefresh={handleFetchDetailPost} onSelect={handlePremiumBlog} />
                    ) : (
                        <Recent data={data} onClick={(e) => (window.location = e?.data?.permalink)} />
                    )}
                </Tab>
                <Tab eventKey="categories" active={active}>
                    {selectedCategory ? (
                        <CategoryFeed category={selectedCategory} posts={data.posts?.data} />
                    ) : (
                        <Categories categories={categories} onDetail={handleGetCategoryDetail} onSelect={handleSelectMenu} />
                    )}
                </Tab>
                <Tab eventKey="departments" active={active}>
                    {selectedDepartment ? (
                        <DepartmentFeed department={selectedDepartment} posts={data.posts?.data} />
                    ) : (
                        <Departments editable={editable} departments={departments} onDetail={handleGetDepartmentDetail} onSelect={handleSelectMenu} />
                    )}
                </Tab>
            </div>
            <div className="d-flex-1 ms-2 ssm-l-0">
                <div className="card mt-0 ssm-t-3">
                    <p className="dsl-b18 bold mt-0 ssm-t-3">Categories</p>
                    {categories.map((category, index) => {
                        const { name } = category;
                        if (allcategories || index < 5) {
                            return (
                                <Button className="dsl-b14 jc-start px-0" key={name} type="link" onClick={() => handleGetCategoryDetail(category)}>
                                    <span className="truncate-one dsl-p14 text-muted">{name}</span>
                                </Button>
                            );
                        }
                        return null;
                    })}
                    {categories.length > 5 && (
                        <div className="d-h-end">
                            <Button type="link" name={allcategories ? 'See less' : 'See All'} onClick={() => setAllCategories(!allcategories)} />
                        </div>
                    )}
                </div>
                <div className="card mt-0 ssm-t-3">
                    <p className="dsl-b18 bold mt-0 ssm-t-3">Departments</p>
                    {departments.map((department, index) => {
                        const { name } = department;
                        return (
                            <Button className="dsl-b14 jc-start px-0 text-start" key={`department-${index}`} type="link" onClick={() => handleGetDepartmentDetail(department)}>
                                <span className="truncate-one dsl-p14 text-muted">{name}</span>
                            </Button>
                        );
                    })}
                </div>
            </div>
        </ErrorBoundary>
    );
};

Blog.propTypes = {
    editable: PropTypes.bool,
    data: PropTypes.any.isRequired,
};

Blog.defaultProps = {
    editable: false,
    data: {},
};

export default Blog;
